/**
 * Card de produto em formato grid - Somente visualização
 * Exibe informações de previsão, pedido, estoque e percentual de venda
 */

import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  LinearProgress,
} from '@mui/material';
import { Inventory } from '@mui/icons-material';
import { Produto } from '@types';
import { FormatUtil } from '@utils';
import { LazyCardMedia } from '@components/common';
import theme from '@theme';

interface ProdutoItemGridProps {
  produto: Produto;
}

export const ProdutoItemGrid: React.FC<ProdutoItemGridProps> = React.memo(({ produto }) => {
  const getImageUrl = (filename: string): string => {
    if (!filename) return '';
    return FormatUtil.formatImageUrl(`${filename}`);
  };

  // Calcula percentual de venda (pedido / previsão * 100)
  const previsao = produto.previsao || 0;
  const pedido = produto.pedido || 0;
  const percentual = previsao > 0 ? (pedido / previsao) * 100 : 0;
  const corPercentual = percentual >= 50 ? 'success.main' : 'error.main';

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.3s',
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      <LazyCardMedia
        height="140"
        image={getImageUrl(produto.imagem)}
        alt={produto.nome}
        loading="lazy"
        sx={{
          objectFit: 'cover',
          bgcolor: 'grey.100',
          backgroundColor: theme.palette.elite.contrastText,
        }}
      />

      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 1.5 }}>
        {/* Código e Nome */}
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: '0.65rem', display: 'block', mb: 0.3 }}
          >
            Cód: {produto.codigo}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              fontSize: '0.8rem',
              lineHeight: 1.2,
              minHeight: '2.4rem',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {produto.nome}
          </Typography>
        </Box>

        {/* Chips de informação */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5 }}>
            <Chip
              size="small"
              label={`Prev: ${previsao}`}
              color="primary"
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20, flex: 1 }}
            />
            <Chip
              size="small"
              label={`Ped: ${pedido}`}
              color={Number(produto.estoque) > 0 ? 'secondary' : 'success'}
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20, flex: 1 }}
            />
          </Box>
          <Chip
            size="small"
            icon={<Inventory sx={{ fontSize: '0.75rem' }} />}
            label={`Est: ${produto.estoque}`}
            color={Number(produto.estoque) > 0 ? 'error' : 'success'}
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Box>

        {/* Percentual com barra de progresso */}
        <Box sx={{ mt: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              Vendido
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                color: corPercentual,
                fontSize: '0.75rem',
              }}
            >
              {percentual.toFixed(1)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(percentual, 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                backgroundColor: corPercentual,
                borderRadius: 3,
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
});

ProdutoItemGrid.displayName = 'ProdutoItemGrid';
